// py) and reprojection to lng/lat is deterministic, so two rook-4 neighbors share an edge with
// bit-identical endpoints → adjacency is exact (we still round to ~1 mm defensively).

type Pt = [number, number];
// accepts any GeoJSON geometry (coordinates optional so a GeometryCollection type-checks; it yields no rings)
type Geom = { type: string; coordinates?: unknown } | undefined;

// every OUTER ring of a Polygon/MultiPolygon (cells have no holes → inner rings don't matter for edges)
export function outerRings(geom: Geom): Pt[][] {
  if (!geom) return [];
  if (geom.type === "Polygon") {
    const r = (geom.coordinates as Pt[][])[0];
    return r ? [r] : [];
  }
  if (geom.type === "MultiPolygon")
    return (geom.coordinates as Pt[][][])
      .map((poly) => poly[0])
      .filter(Boolean);
  return [];
}

const rnd = (v: number) => Math.round(v * 1e8) / 1e8;
// canonical, direction-independent key for the undirected edge a–b
export function edgeKey(a: Pt, b: Pt): string {
  const ax = rnd(a[0]),
    ay = rnd(a[1]),
    bx = rnd(b[0]),
    by = rnd(b[1]);
  return ax < bx || (ax === bx && ay <= by)
    ? `${ax},${ay};${bx},${by}`
    : `${bx},${by};${ax},${ay}`;
}

// Rook-4 neighbor index list per cell: two cells are adjacent iff they share an edge. O(cells · edges).
export function cellAdjacency(geoms: Geom[]): number[][] {
  const adj: number[][] = geoms.map(() => []);
  const byEdge = new Map<string, number[]>();
  for (let i = 0; i < geoms.length; i++) {
    for (const ring of outerRings(geoms[i])) {
      const n = ring.length;
      for (let e = 0; e < n; e++) {
        const a = ring[e];
        const b = ring[(e + 1) % n];
        if (rnd(a[0]) === rnd(b[0]) && rnd(a[1]) === rnd(b[1])) continue; // zero-length (ring closure)
        const key = edgeKey(a, b);
        const arr = byEdge.get(key);
        if (arr) arr.push(i);
        else byEdge.set(key, [i]);
      }
    }
  }
  for (const arr of byEdge.values()) {
    // an edge shared by two cells → they're neighbors (dedup: a cell can't list the same edge twice)
    for (let x = 0; x < arr.length; x++)
      for (let y = x + 1; y < arr.length; y++)
        if (arr[x] !== arr[y]) {
          adj[arr[x]].push(arr[y]);
          adj[arr[y]].push(arr[x]);
        }
  }
  return adj;
}
